import { create } from 'zustand';
import type {
  Role, Theme, ProfileTab, ModalState, Toast, ClockQueueItem,
} from '../types';

const SESSION_KEY = 'skpeople.session';

function parseJwtClaims(token: string): Record<string, unknown> {
  try {
    const [, b64] = token.split('.');
    return JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return {}; }
}

function loadSession() {
  if (typeof localStorage === 'undefined') return null;
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch { return null; }
}

function saveSession(data: object) {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch {}
}

function clearSession() {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

interface AppState {
  // Session
  role: Role;
  baseRole: Role;
  profileId: string;
  /** platform_org_id claim from the JWT — needed to call platform org APIs (e.g. invitations). */
  orgId: string;
  /** org_name claim from the JWT — the tenant's display name (e.g. "Softknack"). */
  orgName: string;
  /** DB UUID of the Employee record linked to this account. Resolved at runtime via /employees/me. */
  employeeId: string;
  firstName: string;
  displayName: string;
  email: string;
  theme: Theme;
  isAuthenticated: boolean;
  token: string | null;

  // Navigation
  profileTab: ProfileTab;

  // UI State
  copilotOpen: boolean;
  modal: ModalState;
  toasts: Toast[];

  // Clock queue for optimistic punch UX
  clockQueue: ClockQueueItem[];

  // Actions
  login: (token: string, role: Role, profileId: string, firstName?: string, displayName?: string, email?: string, orgId?: string, orgName?: string) => void;
  logout: () => void;
  setEmployeeId: (id: string) => void;
  setRole: (role: Role) => void;
  setProfileTab: (tab: ProfileTab) => void;
  toggleTheme: () => void;
  toggleCopilot: () => void;
  openModal: (type: ModalState['type'], props?: Record<string, unknown>) => void;
  closeModal: () => void;
  toast: (message: string, variant?: Toast['variant']) => void;
  dismissToast: (id: string) => void;

  // Clock queue actions
  addToClockQueue: (item: ClockQueueItem) => void;
  resolveClockQueue: (tempId: string) => void;
  failClockQueue: (tempId: string) => void;
}

const saved = loadSession();

export const useAppStore = create<AppState>((set, get) => ({
  role: saved?.role ?? 'ess',
  baseRole: saved?.baseRole ?? saved?.role ?? 'ess',
  profileId: saved?.profileId ?? 'emp_001',
  orgId: saved?.orgId ?? '',
  orgName: saved?.orgName ?? '',
  employeeId: '',
  firstName: saved?.firstName ?? '',
  displayName: saved?.displayName ?? '',
  email: saved?.email ?? '',
  theme: saved?.theme ?? 'light',
  isAuthenticated: !!(saved?.hasSession),
  token: null,
  profileTab: 'overview',
  copilotOpen: false,
  modal: { type: null },
  toasts: [],
  clockQueue: [],

  login: (token, role, profileId, firstName = '', displayName = '', email = '', orgId = '', orgName = '') => {
    const claims = parseJwtClaims(token);
    const resolvedOrgId = orgId || (claims.platform_org_id as string) || '';
    const resolvedOrgName = orgName || (claims.org_name as string) || '';
    set({ token, role, baseRole: role, profileId, orgId: resolvedOrgId, orgName: resolvedOrgName, firstName, displayName, email, isAuthenticated: true });
    saveSession({ hasSession: true, role, baseRole: role, profileId, orgId: resolvedOrgId, orgName: resolvedOrgName, firstName, displayName, email, theme: get().theme });
  },

  logout: () => {
    clearSession();
    set({
      token: null,
      isAuthenticated: false,
      role: 'ess',
      baseRole: 'ess',
      profileId: 'emp_001',
      orgName: '',
      employeeId: '',
      firstName: '',
      displayName: '',
      email: '',
    });
  },

  setEmployeeId: (id) => set({ employeeId: id }),

  setRole: (role) => {
    set({ role, modal: { type: null } });
    saveSession({ hasSession: true, role, baseRole: get().baseRole, profileId: get().profileId, orgId: get().orgId, orgName: get().orgName, firstName: get().firstName, displayName: get().displayName, email: get().email, theme: get().theme });
  },

  setProfileTab: (tab) => set({ profileTab: tab }),

  toggleTheme: () => {
    const next = get().theme === 'light' ? 'dark' : 'light';
    set({ theme: next });
    saveSession({ hasSession: true, role: get().role, baseRole: get().baseRole, profileId: get().profileId, orgId: get().orgId, orgName: get().orgName, firstName: get().firstName, displayName: get().displayName, email: get().email, theme: next });
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
    }
  },

  toggleCopilot: () => set((s) => ({ copilotOpen: !s.copilotOpen })),

  openModal: (type, props) => set({ modal: { type, props } }),
  closeModal: () => set({ modal: { type: null } }),

  toast: (message, variant = 'success') => {
    const id = `toast_${Math.random().toString(36).slice(2)}`;
    set((s) => ({ toasts: [...s.toasts, { id, message, variant }] }));
    setTimeout(() => get().dismissToast(id), 4000);
  },

  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  addToClockQueue: (item) => set((s) => ({ clockQueue: [...s.clockQueue, item] })),

  resolveClockQueue: (tempId) =>
    set((s) => ({
      clockQueue: s.clockQueue.map((q) => q.tempId === tempId ? { ...q, status: 'synced' as const } : q),
    })),

  failClockQueue: (tempId) =>
    set((s) => ({
      clockQueue: s.clockQueue.map((q) => q.tempId === tempId ? { ...q, status: 'failed' as const } : q),
    })),
}));
